import React, { useMemo } from "react";
import AssigneesAvatarGroup, {
  type AssigneeAvatarItem,
} from "components/common/AssigneesAvatarGroup";
import type {
  PettyWork,
  PettyWorkAssignmentSummary,
} from "features/PettyWorks/types/petty-work.types";

function assigneeLabel(a: PettyWorkAssignmentSummary): string {
  const u = a.user;
  const name = [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (u?.email) return u.email;
  const id = u?.id ?? a.toUserId;
  return id > 0 ? `User #${id}` : "—";
}

function assigneeUserId(a: PettyWorkAssignmentSummary): number | null {
  const raw = a.user?.id ?? a.toUserId;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}


function dedupeKey(a: PettyWorkAssignmentSummary): number {
  const uid = assigneeUserId(a);
  if (uid != null) return uid;
  const id = Number(a.id);
  return Number.isFinite(id) && id > 0 ? id : 0;
}

export default function PettyWorkAssigneesAvatarGroup({
  pettyWork,
  assignments,
  pettyWorkId,
  canReadDetail,
  canAssignMore,
  onOpenAssign,
}: {
  pettyWork: PettyWork;
  assignments?: PettyWorkAssignmentSummary[] | null;
  pettyWorkId: number;
  canReadDetail: boolean;
  canAssignMore: boolean;
  onOpenAssign: (pw: PettyWork) => void;
}) {
  const assignees = useMemo<AssigneeAvatarItem[]>(() => {
    const list = (assignments ?? []).filter((a) => !a.deleted_at);
    const map = new Map<number, PettyWorkAssignmentSummary>();
    for (const a of list) {
      const k = dedupeKey(a);
      if (k > 0 && !map.has(k)) map.set(k, a);
    }
    return Array.from(map.values()).map((a) => ({
      key: dedupeKey(a) || a.id,
      userId: assigneeUserId(a),
      label: assigneeLabel(a),
      avatar: a.user?.avatar,
      employee: a.user?.employee,
    }));
  }, [assignments]);

  const detailTo = `/petty-works/${pettyWorkId}`;

  return (
    <AssigneesAvatarGroup
      assignees={assignees}
      detailTo={detailTo}
      canReadDetail={canReadDetail}
      canAssignMore={canAssignMore}
      onOpenAssign={() => onOpenAssign(pettyWork)}
      emptyTitle="No assignees — open record to assign"
      addTitle="Assign user"
      showAddWhenLessThan={5}
    />
  );
}
